/**
 * TicketPresencialModal — Kiosko de auto-registro estilo TypeForm.
 *
 * 4 fases:
 *   1) Datos personales: Tipo doc, N° doc, Nombre, Email.
 *   2) Proyecto + Motivo (botones grandes apilados).
 *   3) Descripción del motivo (textarea).
 *   4) Confirmación con código de ticket.
 *
 * Comportamiento:
 *   - Fullscreen overlay, touch-friendly (iPad/tablet en recepción).
 *   - Enter avanza a la siguiente fase (si validación pasa).
 *   - Auto-reset tras 15s en Fase 4 (modo kiosk).
 *   - Sin persistencia en localStorage entre clientes (privacidad).
 *   - Debounce del botón Siguiente/Confirmar.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../../context/AuthContext';
import {
  X, ArrowLeft, ArrowRight, Check, Loader2, AlertCircle, IdCard,
  Building2, Tag, MessageSquare, Sparkles, Maximize2, Minimize2,
} from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../components/ui/select';
import { Textarea } from '../../../components/ui/textarea';
import { toast } from 'sonner';

const TIPOS_DOC = [
  { value: 'DNI',       label: 'DNI' },
  { value: 'CE',        label: 'Carnet de Extranjería' },
  { value: 'PASAPORTE', label: 'Pasaporte' },
  { value: 'RUC',       label: 'RUC' },
];

const AUTO_RESET_MS = 15_000;
const TOTAL_STEPS = 4;

const initialState = () => ({
  cliente_tipo_documento: 'DNI',
  cliente_documento: '',
  cliente_nombre: '',
  cliente_email: '',
  proyecto_id: '',
  clasificacion_id: '',
  descripcion_motivo: '',
});

/** Valida formato de documento client-side (espejo del backend). */
function validarDoc(tipo, num) {
  const n = (num || '').trim();
  if (!n) return 'El número de documento es requerido.';
  if (tipo === 'DNI' && !/^\d{8}$/.test(n)) return 'El DNI debe tener 8 dígitos.';
  if (tipo === 'RUC' && !/^\d{11}$/.test(n)) return 'El RUC debe tener 11 dígitos.';
  if (tipo === 'RUC' && !/^(10|15|16|17|20)/.test(n)) return 'El RUC debe iniciar con 10, 15, 16, 17 o 20.';
  if (tipo === 'CE' && (n.length < 9 || n.length > 20)) return 'El CE debe tener entre 9 y 20 caracteres.';
  if (tipo === 'PASAPORTE' && (n.length < 6 || n.length > 20)) return 'El pasaporte debe tener entre 6 y 20 caracteres.';
  if ((tipo === 'CE' || tipo === 'PASAPORTE') && !/^[a-zA-Z0-9]+$/.test(n)) return 'Solo letras y números.';
  // Todos iguales / secuencial (heurística)
  if (new Set(n).size === 1) return 'El número no es válido.';
  return null;
}

function validarEmail(email) {
  if (!email) return 'El correo es requerido.';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return 'Correo inválido.';
  return null;
}

export default function TicketPresencialModal({ open, onClose, onCreated }) {
  const { api } = useAuth();
  const [step, setStep] = useState(1);
  const [data, setData] = useState(initialState);
  const [proyectos, setProyectos] = useState([]);
  const [motivos, setMotivos] = useState([]);
  const [loadingCatalogs, setLoadingCatalogs] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState('');
  const [resultado, setResultado] = useState(null); // { codigo, cliente_nombre }
  const [fullscreen, setFullscreen] = useState(() => {
    try { return localStorage.getItem('tk-kiosk-fullscreen') === '1'; }
    catch { return false; }
  });
  const firstInputRef = useRef(null);

  // Cargar catálogos al abrir
  useEffect(() => {
    if (!open) return;
    let cancel = false;
    setLoadingCatalogs(true);
    Promise.all([
      api.get('/proyectos'),
      api.get('/tickets/kiosk/motivos'),
    ]).then(([pRes, mRes]) => {
      if (cancel) return;
      setProyectos(pRes.data || []);
      setMotivos(mRes.data || []);
    }).catch((e) => {
      if (!cancel) toast.error('Error cargando catálogos');
      // eslint-disable-next-line no-console
      console.error(e);
    }).finally(() => { if (!cancel) setLoadingCatalogs(false); });
    return () => { cancel = true; };
  }, [open, api]);

  // Reset cuando se cierra
  useEffect(() => {
    if (!open) {
      setStep(1);
      setData(initialState());
      setErrors({});
      setServerError('');
      setResultado(null);
    }
  }, [open]);

  // Autofocus en cada fase
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      try { firstInputRef.current?.focus(); } catch { /* noop */ }
    }, 250); // espera a que la transición termine
    return () => clearTimeout(t);
  }, [step, open]);

  // Auto-reset tras éxito (Fase 4) — modo kiosk
  useEffect(() => {
    if (step !== 4 || !resultado) return undefined;
    const t = setTimeout(() => {
      setStep(1);
      setData(initialState());
      setErrors({});
      setServerError('');
      setResultado(null);
    }, AUTO_RESET_MS);
    return () => clearTimeout(t);
  }, [step, resultado]);

  // Fullscreen: persistir + sincronizar con Fullscreen API del navegador.
  const toggleFullscreen = () => setFullscreen((v) => !v);

  useEffect(() => {
    if (!open) return undefined;
    try { localStorage.setItem('tk-kiosk-fullscreen', fullscreen ? '1' : '0'); }
    catch { /* noop */ }
    // Sincronizar con Fullscreen API del navegador (iPad/Safari/Chrome).
    const isInFs = !!document.fullscreenElement;
    if (fullscreen && !isInFs) {
      try { document.documentElement.requestFullscreen?.().catch(() => {}); } catch { /* noop */ }
    } else if (!fullscreen && isInFs) {
      try { document.exitFullscreen?.().catch(() => {}); } catch { /* noop */ }
    }
    return undefined;
  }, [fullscreen, open]);

  // Sincronizar state si el usuario sale de fullscreen con Esc del navegador.
  useEffect(() => {
    if (!open) return undefined;
    const onFsChange = () => {
      if (!document.fullscreenElement && fullscreen) setFullscreen(false);
    };
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, [open, fullscreen]);

  // Atajos: F11 toggle, Esc sale solo de fullscreen (sin cerrar modal).
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (ev) => {
      if (ev.key === 'F11') {
        ev.preventDefault();
        toggleFullscreen();
      } else if (ev.key === 'Escape' && fullscreen) {
        ev.preventDefault();
        ev.stopPropagation();
        setFullscreen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, fullscreen]);

  // Validación por fase
  const validarFaseActual = () => {
    const e = {};
    if (step === 1) {
      const docErr = validarDoc(data.cliente_tipo_documento, data.cliente_documento);
      if (docErr) e.cliente_documento = docErr;
      const nm = (data.cliente_nombre || '').trim();
      if (nm.length < 3) e.cliente_nombre = 'Ingresa tu nombre completo.';
      else if (!nm.includes(' ')) e.cliente_nombre = 'Incluye nombre y apellido.';
      const emErr = validarEmail(data.cliente_email);
      if (emErr) e.cliente_email = emErr;
    } else if (step === 2) {
      if (!data.proyecto_id) e.proyecto_id = 'Selecciona un proyecto.';
      if (!data.clasificacion_id) e.clasificacion_id = 'Selecciona un motivo.';
    } else if (step === 3) {
      const d = (data.descripcion_motivo || '').trim();
      if (d.length < 10) e.descripcion_motivo = 'Mínimo 10 caracteres.';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleNext = async () => {
    if (submitting) return;
    setServerError('');
    if (!validarFaseActual()) return;
    if (step < 3) {
      setStep((s) => s + 1);
    } else if (step === 3) {
      // Enviar
      setSubmitting(true);
      try {
        const { data: result } = await api.post('/tickets/kiosk/registrar', data);
        setResultado(result);
        setStep(4);
        onCreated?.();
      } catch (err) {
        const detail = err?.response?.data?.detail || 'Error al registrar el ticket.';
        setServerError(typeof detail === 'string' ? detail : 'Error al registrar.');
      } finally {
        setSubmitting(false);
      }
    }
  };

  const handleBack = () => {
    if (step > 1 && step < 4) setStep((s) => s - 1);
  };

  const handleCancel = () => {
    if (!window.confirm('¿Cancelar el registro? Se perderán los datos ingresados.')) return;
    setStep(1);
    setData(initialState());
    setErrors({});
    setServerError('');
  };

  // Enter para avanzar (excepto en textarea con Shift+Enter, y excepto en Fase 4)
  const onKeyDownShell = (ev) => {
    if (ev.key === 'Enter' && !ev.shiftKey) {
      const tag = (ev.target?.tagName || '').toLowerCase();
      if (tag === 'textarea') return; // permitir saltos de línea en descripción
      if (step === 4) return;
      ev.preventDefault();
      handleNext();
    }
  };

  // Lista de proyectos ordenada
  const proyectosSorted = useMemo(
    () => [...(proyectos || [])].sort((a, b) => (a.nombre || '').localeCompare(b.nombre || '')),
    [proyectos],
  );

  if (!open) return null;

  return (
    <div className="tk-kiosk-overlay" data-testid="kiosk-modal" onKeyDown={onKeyDownShell} role="dialog" aria-modal="true">
      <div className={`tk-kiosk-shell ${fullscreen ? 'is-fullscreen' : ''}`}>
        {/* Header */}
        <header className="tk-kiosk-header">
          <div className="tk-kiosk-brand">
            <Sparkles style={{ width: 16, height: 16 }} />
            <span>Registro de Visita</span>
          </div>
          <div className="tk-kiosk-header-actions">
            <button
              type="button"
              className="tk-kiosk-icon-btn"
              onClick={toggleFullscreen}
              data-testid="kiosk-btn-maximize"
              aria-label={fullscreen ? 'Restaurar tamaño' : 'Pantalla completa'}
              title={fullscreen ? 'Restaurar tamaño (F11)' : 'Pantalla completa (F11)'}
            >
              {fullscreen
                ? <Minimize2 style={{ width: 18, height: 18 }} />
                : <Maximize2 style={{ width: 18, height: 18 }} />}
            </button>
            <button
              type="button"
              className="tk-kiosk-icon-btn"
              onClick={onClose}
              data-testid="kiosk-btn-close"
              aria-label="Cerrar"
            >
              <X style={{ width: 20, height: 20 }} />
            </button>
          </div>
        </header>

        {/* Progress */}
        <div className="tk-kiosk-progress" aria-label={`Paso ${step} de ${TOTAL_STEPS}`}>
          {[1, 2, 3, 4].map((s) => (
            <div
              key={s}
              className={`tk-kiosk-progress-seg ${s < step ? 'is-done' : ''} ${s === step ? 'is-current' : ''}`}
            />
          ))}
          <span className="tk-kiosk-progress-label">Paso {step} de {TOTAL_STEPS}</span>
        </div>

        {/* Stage */}
        <main className="tk-kiosk-stage">
          {loadingCatalogs ? (
            <div className="tk-kiosk-loading">
              <Loader2 className="tk-spin" style={{ width: 32, height: 32 }} />
              <span>Cargando...</span>
            </div>
          ) : (
            <>
              {step === 1 && (
                <section className="tk-kiosk-fase" data-testid="kiosk-step-1">
                  <div className="tk-kiosk-icon-bubble"><IdCard /></div>
                  <h2 className="tk-kiosk-question">¡Hola! Cuéntanos quién eres</h2>
                  <p className="tk-kiosk-helper">Necesitamos tus datos para crear tu ticket de atención.</p>

                  <div className="tk-kiosk-row">
                    <div className="tk-kiosk-field" style={{ flex: '0 0 230px' }}>
                      <label className="tk-kiosk-label">Tipo de documento</label>
                      <Select
                        value={data.cliente_tipo_documento}
                        onValueChange={(v) => setData((d) => ({ ...d, cliente_tipo_documento: v, cliente_documento: '' }))}
                      >
                        <SelectTrigger className="tk-kiosk-input" data-testid="kiosk-select-tipo-doc">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {TIPOS_DOC.map((t) => (
                            <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="tk-kiosk-field" style={{ flex: 1 }}>
                      <label className="tk-kiosk-label">Número de documento</label>
                      <input
                        ref={firstInputRef}
                        type="text"
                        inputMode={data.cliente_tipo_documento === 'DNI' || data.cliente_tipo_documento === 'RUC' ? 'numeric' : 'text'}
                        autoComplete="off"
                        className={`tk-kiosk-input ${errors.cliente_documento ? 'has-error' : ''}`}
                        placeholder={data.cliente_tipo_documento === 'DNI' ? '12345678' : data.cliente_tipo_documento === 'RUC' ? '20123456789' : 'Número'}
                        value={data.cliente_documento}
                        onChange={(e) => setData((d) => ({ ...d, cliente_documento: e.target.value.toUpperCase() }))}
                        data-testid="kiosk-input-numero-doc"
                      />
                      {errors.cliente_documento && <span className="tk-kiosk-error">{errors.cliente_documento}</span>}
                    </div>
                  </div>

                  <div className="tk-kiosk-field">
                    <label className="tk-kiosk-label">Nombre completo</label>
                    <input
                      type="text"
                      autoComplete="name"
                      className={`tk-kiosk-input ${errors.cliente_nombre ? 'has-error' : ''}`}
                      placeholder="Tu nombre y apellido"
                      value={data.cliente_nombre}
                      onChange={(e) => setData((d) => ({ ...d, cliente_nombre: e.target.value }))}
                      data-testid="kiosk-input-nombre"
                    />
                    {errors.cliente_nombre && <span className="tk-kiosk-error">{errors.cliente_nombre}</span>}
                  </div>

                  <div className="tk-kiosk-field">
                    <label className="tk-kiosk-label">Correo electrónico</label>
                    <input
                      type="email"
                      autoComplete="email"
                      inputMode="email"
                      className={`tk-kiosk-input ${errors.cliente_email ? 'has-error' : ''}`}
                      placeholder="tu@correo.com"
                      value={data.cliente_email}
                      onChange={(e) => setData((d) => ({ ...d, cliente_email: e.target.value.trim() }))}
                      data-testid="kiosk-input-email"
                    />
                    {errors.cliente_email && <span className="tk-kiosk-error">{errors.cliente_email}</span>}
                  </div>
                </section>
              )}

              {step === 2 && (
                <section className="tk-kiosk-fase" data-testid="kiosk-step-2">
                  <div className="tk-kiosk-icon-bubble"><Building2 /></div>
                  <h2 className="tk-kiosk-question">¿Por qué proyecto nos visitas?</h2>

                  <div className="tk-kiosk-field">
                    <label className="tk-kiosk-label">Proyecto</label>
                    <Select
                      value={data.proyecto_id}
                      onValueChange={(v) => setData((d) => ({ ...d, proyecto_id: v }))}
                    >
                      <SelectTrigger
                        ref={firstInputRef}
                        className={`tk-kiosk-input ${errors.proyecto_id ? 'has-error' : ''}`}
                        data-testid="kiosk-select-proyecto"
                      >
                        <SelectValue placeholder="Selecciona un proyecto" />
                      </SelectTrigger>
                      <SelectContent>
                        {proyectosSorted.map((p) => (
                          <SelectItem key={p.id} value={p.id}>{p.nombre}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.proyecto_id && <span className="tk-kiosk-error">{errors.proyecto_id}</span>}
                  </div>

                  <div className="tk-kiosk-field">
                    <label className="tk-kiosk-label tk-kiosk-label-lg">
                      <Tag style={{ width: 16, height: 16, marginRight: 6, verticalAlign: '-3px' }} />
                      ¿Cuál es el motivo de tu visita?
                    </label>
                    <div className="tk-kiosk-motivos">
                      {motivos.length === 0 ? (
                        <p className="tk-kiosk-helper">No hay motivos configurados. Contacta a tu administrador.</p>
                      ) : motivos.map((m) => {
                        const sel = data.clasificacion_id === m.clasificacion_id;
                        return (
                          <button
                            key={m.clasificacion_id}
                            type="button"
                            className={`tk-kiosk-motivo-btn ${sel ? 'is-selected' : ''}`}
                            onClick={() => setData((d) => ({ ...d, clasificacion_id: m.clasificacion_id }))}
                            data-testid={`kiosk-motivo-btn-${m.clasificacion_id}`}
                          >
                            <span className="tk-kiosk-motivo-name">{m.nombre}</span>
                            <span className="tk-kiosk-motivo-padre">{m.padre_nombre}</span>
                            {sel && <Check className="tk-kiosk-motivo-check" style={{ width: 18, height: 18 }} />}
                          </button>
                        );
                      })}
                    </div>
                    {errors.clasificacion_id && <span className="tk-kiosk-error">{errors.clasificacion_id}</span>}
                  </div>
                </section>
              )}

              {step === 3 && (
                <section className="tk-kiosk-fase" data-testid="kiosk-step-3">
                  <div className="tk-kiosk-icon-bubble"><MessageSquare /></div>
                  <h2 className="tk-kiosk-question">Cuéntanos un poco más</h2>
                  <p className="tk-kiosk-helper">Describe con tus propias palabras el motivo de tu visita. Esto nos ayuda a atenderte mejor.</p>

                  <div className="tk-kiosk-field">
                    <Textarea
                      ref={firstInputRef}
                      className={`tk-kiosk-input tk-kiosk-textarea ${errors.descripcion_motivo ? 'has-error' : ''}`}
                      placeholder="Ej: Necesito una copia digital de mi contrato firmado..."
                      rows={6}
                      maxLength={5000}
                      value={data.descripcion_motivo}
                      onChange={(e) => setData((d) => ({ ...d, descripcion_motivo: e.target.value }))}
                      data-testid="kiosk-textarea-descripcion"
                    />
                    <div className="tk-kiosk-counter" data-testid="kiosk-desc-counter">
                      {(data.descripcion_motivo || '').length} / 5000
                    </div>
                    {errors.descripcion_motivo && <span className="tk-kiosk-error">{errors.descripcion_motivo}</span>}
                  </div>
                </section>
              )}

              {step === 4 && resultado && (
                <section className="tk-kiosk-fase tk-kiosk-fase-success" data-testid="kiosk-step-4">
                  <div className="tk-kiosk-check-circle">
                    <Check style={{ width: 56, height: 56 }} />
                  </div>
                  <h2 className="tk-kiosk-success-title">¡Gracias, {resultado.cliente_nombre.split(' ')[0]}!</h2>
                  <p className="tk-kiosk-success-text">Tu ticket ha sido registrado.</p>
                  <div className="tk-kiosk-codigo" data-testid="kiosk-success-codigo">
                    <span className="tk-kiosk-codigo-label">Número de ticket</span>
                    <span className="tk-kiosk-codigo-value">{resultado.codigo}</span>
                  </div>
                  <p className="tk-kiosk-success-text">En breve serás atendido por un especialista.</p>
                  <p className="tk-kiosk-success-mini">Te enviamos una copia a tu correo.</p>
                </section>
              )}

              {serverError && (
                <div className="tk-kiosk-server-error" role="alert">
                  <AlertCircle style={{ width: 16, height: 16, flexShrink: 0 }} />
                  <span>{serverError}</span>
                </div>
              )}
            </>
          )}
        </main>

        {/* Footer */}
        {step < 4 && !loadingCatalogs && (
          <footer className="tk-kiosk-footer">
            <button
              type="button"
              className="tk-kiosk-btn-secondary"
              onClick={handleBack}
              disabled={step === 1 || submitting}
              data-testid="kiosk-btn-back"
            >
              <ArrowLeft style={{ width: 16, height: 16 }} /> Atrás
            </button>

            <button
              type="button"
              className="tk-kiosk-btn-cancel"
              onClick={handleCancel}
              disabled={submitting}
              data-testid="kiosk-btn-cancel"
            >
              Cancelar
            </button>

            <button
              type="button"
              className="tk-kiosk-btn-primary"
              onClick={handleNext}
              disabled={submitting}
              data-testid="kiosk-btn-next"
            >
              {submitting ? (
                <><Loader2 className="tk-spin" style={{ width: 16, height: 16 }} /> Enviando...</>
              ) : step === 3 ? (
                <>Confirmar <Check style={{ width: 16, height: 16 }} /></>
              ) : (
                <>Siguiente <ArrowRight style={{ width: 16, height: 16 }} /></>
              )}
            </button>
          </footer>
        )}
      </div>
    </div>
  );
}
